import { Controller } from '@hotwired/stimulus';
import { patch } from '../api';
import { fetchJSON } from '../fetch_json';

const NAV_DEBOUNCE_MS = 500;

export default class extends Controller {
  static targets = ['calendar', 'container', 'draggableEl'];

  static values = {
    newOrderUrl: String,
    shootingsUrl: String,
    appointmentsUrl: String,
    businessHoursUrl: String,
    locale: String,
    newAppointmentUrl: String,
    selectable: Boolean
  };
  debounceTimer = null;
  navDebounceTimer = null;
  pendingNavSteps = 0;
  navBurstActive = false;
  eventSourceAbortControllers = {};

  async connect() {
    const { makeCalendar, openGoToDatePicker } = await import('../calendar_controller_helper');
    this.openGoToDatePicker = openGoToDatePicker;
    this.calendar = makeCalendar({
      locale: this.localeValue,
      calendarTarget: this.calendarTarget,
      draggableElements: this.draggableElTargets,
      eventSources: [
        this.abortableEventSource('shootingsEventSource', this.shootingsUrlValue),
        this.abortableEventSource('appointmentsEventSource', this.appointmentsUrlValue, {
          color: '#d88737',
          editable: true
        }),
        this.abortableEventSource('businessHoursEventSource', this.businessHoursUrlValue, {
          color: '#ff0000',
          editable: false,
          display: 'background'
        })
      ],
      eventStartEditable: true,
      eventDrop: this.eventDrop,
      drop: this.drop,
      eventContent: this.eventContent,
      eventMouseEnter: this.eventMouseEnter,
      eventMouseLeave: this.eventMouseLeave,
      leftHeader: 'fullscreen,appointments,privacyMode',
      customButtons: this.customButtons,
      selectable: this.selectableValue,
      select: this.select,
      eventClick: this.eventClick,
      eventResize: this.eventDrop
    });
    this.calendar.render();
    // Default: appointments visible, privacy mode ON
    this.appointmentsVisible = true;
    this.privacyModeActive = true;

    // Apply initial privacy mode state to existing elements
    if (this.privacyModeActive) {
      document.querySelectorAll('.staff-calendar__head').forEach((event) => {
        event.classList.add('visually-hidden');
      });
    }

    // Apply initial appointments visibility state
    if (!this.appointmentsVisible) {
      document.querySelectorAll('.appointment-event').forEach((event) => {
        event.classList.add('visually-hidden');
      });
    }

    setTimeout(() => {
      this.updateButtonTexts();
    }, 100);

    const now = new Date();
    this.calendar.scrollToTime({ hour: now.getHours() });
  }

  disconnect() {
    clearTimeout(this.navDebounceTimer);
    clearTimeout(this.debounceTimer);
    this.navBurstActive = false;
    this.pendingNavSteps = 0;
    Object.values(this.eventSourceAbortControllers).forEach((controller) => controller.abort());
    this.eventSourceAbortControllers = {};
  }

  // Custom events fn + AbortController: rapid prev/next aborts superseded week fetches
  // so skipped ranges do not keep hitting the DB (incident 2026-07-18).
  abortableEventSource(id, url, extras = {}) {
    return {
      id,
      ...extras,
      events: (fetchInfo, successCallback, failureCallback) => {
        this.eventSourceAbortControllers[id]?.abort();
        const controller = new AbortController();
        this.eventSourceAbortControllers[id] = controller;

        const requestUrl = new URL(url, window.location.origin);
        requestUrl.searchParams.set('start', fetchInfo.startStr);
        requestUrl.searchParams.set('end', fetchInfo.endStr);

        fetchJSON(requestUrl, {
          signal: controller.signal,
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        })
          .then(successCallback)
          .catch((error) => {
            if (error.name === 'AbortError') return;
            failureCallback(error);
          });
      }
    };
  }

  queueNavigate = (direction) => {
    this.pendingNavSteps += direction;
    clearTimeout(this.navDebounceTimer);

    // First click in a burst navigates immediately; further clicks coalesce.
    if (!this.navBurstActive) {
      this.navBurstActive = true;
      this.flushNavigate();
    }

    this.navDebounceTimer = setTimeout(() => {
      this.navBurstActive = false;
      this.flushNavigate();
    }, NAV_DEBOUNCE_MS);
  };

  flushNavigate = () => {
    const steps = this.pendingNavSteps;
    this.pendingNavSteps = 0;
    if (!steps || !this.calendar) return;

    if (steps > 0) {
      for (let i = 0; i < steps; i += 1) this.calendar.next();
    } else {
      for (let i = 0; i < -steps; i += 1) this.calendar.prev();
    }
  };

  eventClick = (info) => {
    info.jsEvent.preventDefault();
    switch (info.event.source.id) {
      case 'appointmentsEventSource':
        document.getElementById('modal').setAttribute('src', info.event.url);
        break;
      case 'shootingsEventSource':
        window.open(info.event.url);
    }
  };

  eventDrop = async (eventDropInfo) => {
    if (
      eventDropInfo.event.start === eventDropInfo.oldEvent.start &&
      eventDropInfo.event.end === eventDropInfo.oldEvent.end
    )
      return;
    try {
      if (!confirm(window.I18n.confirmMoveEvent)) {
        throw new Error();
      }
      const eventSource = eventDropInfo.event.source;
      const requestBody =
        eventSource.id === 'appointmentsEventSource'
          ? {
              appointment: {
                starts_at: eventDropInfo.event.startStr.substring(0, 19),
                stops_at: eventDropInfo.event.endStr.substring(0, 19)
              }
            }
          : {
              shootings_update_shooting_form: {
                ...eventDropInfo.event.extendedProps.formAttributes,
                starts_at: eventDropInfo.event.startStr.substring(0, 19)
              }
            };
      const newEvent = await patch(
        eventDropInfo.event.extendedProps.updateUrl,
        requestBody
      );
      eventDropInfo.event.remove();
      this.calendar.addEvent(newEvent, eventSource.id);
      this.calendar.refetchEvents();
    } catch (error) {
      eventDropInfo.revert();
    }
  };

  /*
   * This is only for new events that are dropped
   */
  drop = (dropInfo) => {
    // Note: Draggable elements (shooting variants) don't have discount constraints
    // Discount validation only applies to existing shooting events with discounts
    const eventData = JSON.parse(dropInfo.draggedEl.dataset.event);

    if (eventData.discount_validity && dropInfo.dateStr) {
      const dropDate = new Date(dropInfo.dateStr);
      const validFrom = eventData.discount_validity.valid_from
        ? new Date(eventData.discount_validity.valid_from)
        : null;
      const validThrough = eventData.discount_validity.valid_through
        ? new Date(eventData.discount_validity.valid_through)
        : null;

      // If no validity period is set, allow the drop
      if (!validFrom && !validThrough) {
        return true;
      }

      // Check if drop date is within the validity period
      const afterValidFrom = !validFrom || dropDate >= validFrom;
      const beforeValidThrough = !validThrough || dropDate <= validThrough;
      const isValidDrop = afterValidFrom && beforeValidThrough;

      if (!isValidDrop) {
        alert(
          'Dieses Shooting kann nicht an diesem Tag erstellt werden. Bitte Rabatt-Details prüfen. Gültigkeitszeitraum: ' +
            validFrom.toLocaleDateString('de-DE') +
            ' - ' +
            validThrough.toLocaleDateString('de-DE')
        );
        return;
      }
    }
    this.createNewShootingEvent(eventData, dropInfo.dateStr, dropInfo.draggedEl);
  };

  createNewShootingEvent(event, start, draggedEl) {
    this.newShootingEvent = this.calendar.addEvent({
      ...event,
      start: Date.parse(start),
      end: Date.parse(start) + event.duration
    });

    const url = new URL(this.newOrderUrlValue);
    url.searchParams.set('starts_at', start);
    url.searchParams.set('shooting_category_id', event.shooting_category_id);
    url.searchParams.set('shooting_variant_id', event.shooting_variant_id);

    document.getElementById('modal').setAttribute('src', url);

    draggedEl.remove();
  }

  eventMouseEnter = (mouseEnterInfo) => {
    const el = mouseEnterInfo.el;
    const event = mouseEnterInfo.event;
    this.debounce(() => {
      el.parentNode.classList.add('calendar-zoom');
      if (event.extendedProps.eventColor) {
        el.style.background = event.extendedProps.eventColor;
      }
    }, 750)();
  };

  eventMouseLeave = (mouseLeaveInfo) => {
    const el = mouseLeaveInfo.el;
    const event = mouseLeaveInfo.event;
    clearTimeout(this.debounceTimer);
    el.parentNode.classList.remove('calendar-zoom');
    if (event.extendedProps.background) {
      el.style.background = event.extendedProps.background;
    }
  };

  eventContent = ({ event }, createElement) => {
    let text = createElement('p', {}, event.title);
    // Apply visually-hidden class if privacy mode is active
    const headClass = this.privacyModeActive
      ? 'staff-calendar__head visually-hidden'
      : 'staff-calendar__head';
    let title = createElement('div', { class: headClass }, text);
    let startStopTime = createElement(
      'h4',
      {},
      `${event.start.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })} - ${event.end.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })}`
    );
    let wrapper = createElement('div', { class: 'fc-event-main-frame staff-calendar' }, [
      startStopTime,
      title
    ]);

    return wrapper;
  };

  select = (selectionInfo) => {
    const url = new URL(this.newAppointmentUrlValue);
    url.searchParams.set(
      'appointment_form[starts_at]',
      selectionInfo.startStr.substring(0, 19)
    );
    url.searchParams.set(
      'appointment_form[stops_at]',
      selectionInfo.endStr.substring(0, 19)
    );
    document.getElementById('modal').setAttribute('src', url);
  };

  toggleAppointments = () => {
    document.querySelectorAll('.appointment-event').forEach((event) => {
      event.classList.toggle('visually-hidden');
    });

    this.appointmentsVisible = !this.appointmentsVisible;
    this.updateButtonTexts();
  };

  togglePrivacyMode = () => {
    // Original logic: toggle visibility
    document.querySelectorAll('.staff-calendar__head').forEach((event) => {
      event.classList.toggle('visually-hidden');
    });

    // Track state for button text and persist to localStorage
    this.privacyModeActive = !this.privacyModeActive;
    this.updateButtonTexts();
  };

  updateButtonTexts = () => {
    if (!this.calendar) {
      console.warn('Calendar not initialized yet');
      return;
    }

    try {
      // Appointments button: visible = show "ausblenden" (the action to hide)
      const appointmentsText = this.appointmentsVisible
        ? window.I18n.appointmentsVisible || 'Termine: ausblenden'
        : window.I18n.appointmentsHidden || 'Termine: einblenden';

      // Privacy mode button: active = show "AN"
      const privacyText = this.privacyModeActive
        ? window.I18n.privacyModeOn || 'Privatmodus: AN'
        : window.I18n.privacyModeOff || 'Privatmodus: AUS';

      // Update all button texts via FullCalendar API
      this.calendar.setOption('customButtons', this.buildCustomButtons({
        appointmentsText,
        privacyText
      }));
    } catch (error) {
      console.error('Error updating button texts:', error);
    }
  };

  toggleFullscreen = () => {
    if (!this.containerTarget.classList.contains('calendar__container--fullscreen')) {
      this.containerTarget.classList.add('calendar__container--fullscreen');
      this.calendar.setOption('height', '100%');
      return;
    }

    this.containerTarget.classList.remove('calendar__container--fullscreen');
    this.calendar.setOption('height', undefined);
  };

  buildCustomButtons = ({
    appointmentsText = window.I18n.appointmentsVisible || 'Termine: ausblenden',
    privacyText = window.I18n.privacyModeOn || 'Privatmodus: AN'
  } = {}) => ({
    prev: {
      icon: 'chevron-left',
      click: () => this.queueNavigate(-1)
    },
    next: {
      icon: 'chevron-right',
      click: () => this.queueNavigate(1)
    },
    goToDate: {
      text: window.I18n.goToDate || 'Datum',
      click: () => this.openGoToDatePicker(this.calendar, this.localeValue)
    },
    appointments: {
      text: appointmentsText,
      click: this.toggleAppointments
    },
    fullscreen: {
      text: window.I18n.toggleFullscreen,
      click: this.toggleFullscreen
    },
    privacyMode: {
      text: privacyText,
      click: this.togglePrivacyMode
    }
  });

  get customButtons() {
    return this.buildCustomButtons();
  }

  modalSuccess(_event) {
    this.calendar.getEventSourceById('appointmentsEventSource').refetch();
  }

  debounce(func, timeout) {
    return (...args) => {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        func.apply(this, args);
      }, timeout);
    };
  }
}
